import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useNotifications, ValidationResult } from "sprint-common-web";

import { CloudOrdersValidationResponse } from "../../../common/api/orders-api/order/ValidateOrdersFromImportResponse";
import { useOrdersApi } from "../../../common/api/orders-api/useOrdersApi";
import { orderListingRoute } from "../../OrdersRoutes";
import { blobToBase64 } from "../common/blobToBase64";
import {
    mapToProjectedOrderItems,
    ProjectedOrderItem
} from "./validation-result/success-summary/BuzzOrderProjectedOrderItem";

export interface GroupedFailures {
    code: string;
    failures: string[];
}

export const useImportOrdersForm = () => {
    const [groupedCloudOrdersFailures, setGroupedCloudOrdersFailures] = useState<GroupedFailures[]>([]);
    const [projectedCloudOrders, setProjectedCloudOrders] = useState<ProjectedOrderItem[]>([]);
    const [columnsFailures, setColumnsFailures] = useState<string[]>([]);
    const [encodedFile, setEncodedFile] = useState<string>("");
    const [isFileValidating, setIsFileValidating] = useState<boolean>(false);
    const [isFileValid, setIsFileValid] = useState<boolean>(false);
    const [fileMetadataFailures, setFileMetadataFailures] = useState<string[]>([]);

    const { validateOrdersFromImport, createOrdersFromImport } = useOrdersApi();

    const history = useHistory();
    const { success, error } = useNotifications();

    const isFileValidated =
        !isFileValidating &&
        (!!groupedCloudOrdersFailures.length || !!columnsFailures.length || !!fileMetadataFailures.length);

    const importOrderList = async () => {
        await createOrdersFromImport({ encodedFile })
            .then(() => {
                success("Import succeeded!");
                goToOrderListing();
            })
            .catch(() => {
                error("Import failed!");
            });
    };

    const handleFileMetadataError = (validationResult: ValidationResult): void => {
        success("Validation done!");
        if (validationResult.isValid) {
            return;
        }

        setFileMetadataFailures(validationResult.failures.map((failure) => failure.message));
        setColumnsFailures([]);
        setGroupedCloudOrdersFailures([]);
        setProjectedCloudOrders([]);

        setIsFileValid(false);
        setIsFileValidating(false);
    };

    const validateFile = async (file: File): Promise<void> => {
        setIsFileValidating(true);
        setFileMetadataFailures([]);

        const encodedOrderFile = await blobToBase64(file);

        setEncodedFile(encodedOrderFile);
        validateOrdersFromImport({ encodedFile: encodedOrderFile })
            .then((response) => {
                if (response.isValid) {
                    setIsFileValid(true);
                    setProjectedCloudOrders(
                        mapToProjectedOrderItems({ projectedOrders: response.projectedCloudOrders })
                    );
                } else {
                    setIsFileValid(false);
                    setGroupedCloudOrdersFailures(
                        mapToGroupedFailures({ cloudOrdersValidation: response.cloudOrdersValidation })
                    );
                    setColumnsFailures(response.columnsValidation.failures.map((failure) => failure.message));
                }
                success("Validation done!");
            })
            .catch(() => {
                error("Validation failed!");
            })
            .finally(() => {
                setIsFileValidating(false);
            });
    };

    const goToOrderListing = () => {
        history.push(orderListingRoute.path);
    };

    return {
        groupedCloudOrdersFailures,
        columnsFailures,
        fileMetadataFailures,
        projectedCloudOrders,
        isFileValidating,
        isFileValid,
        isFileValidated,
        encodedFile,
        goToOrderListing,
        validateFile,
        importOrderList,
        handleFileMetadataError
    };
};

const mapToGroupedFailures = ({
    cloudOrdersValidation
}: {
    cloudOrdersValidation: CloudOrdersValidationResponse;
}): GroupedFailures[] => {
    const result: GroupedFailures[] = [];

    Object.entries(cloudOrdersValidation).forEach(([row, validationResult]) => {
        if (validationResult.isValid === false) {
            result.push({
                code: `Row ${row}`,
                failures: validationResult.failures.map((failure) => failure.message)
            });
        }
    });

    return result;
};
